<template>
  <!-- Error notification -->
  <div v-if="messageError" class="notification is-danger actionNotification">
    <button class="delete" @click="handleRemoveError"></button>
    {{ messageError }}
  </div>
  
  <!-- Success notification -->
  <div v-if="messageSuccess" class="notification is-success actionNotification">
    <button class="delete" @click="handleRemoveSuccess"></button>
    {{ messageSuccess }}
  </div>
  
  <!-- Loading indicator -->
  <div v-if="loading" class="container is-fluid" style="padding: 2rem; text-align: center;">
    <div class="loader" style="margin: 2rem auto;"></div>
    <p>Chargement des points de contrôle...</p>
  </div>
  
  <form v-else data-component="Lot/NewVisit" class="container is-fluid" @submit.prevent="handleSubmit">
      <header class="is-flex">
        <h2>Fiche Visite - Logement disponible</h2>
        <CloseButton :to="`/dossier/${propertyId}/timeline`" :doBack="false" />
      </header>

      <hr class="is-short-line" />
      
      <!-- Listing -->
      <div class="listing">
        <!-- No control points message -->
        <div v-if="controlPoints.length === 0" class="notification is-warning" style="margin: 2rem 0;">
          <p>Aucun point de contrôle disponible.</p>
        </div>
        
        <!-- Listing table header -->
        <div v-if="controlPoints.length > 0" class="header">
          <div class="is-flex">
            <div class="flex-grow-[10]">
              <p>Points de contrôle</p>
            </div>
            <div class="column">
              <p>ACTIONS</p>
            </div>
          </div>
        </div>

        <!-- Listing table body -->
        <div v-if="controlPoints.length > 0" class="body">
          <div 
            v-for="(controlPoint, i) in controlPoints" 
            :key="`line-${controlPoint.id}`" 
            class="line"
          >
            <div class="columns">
              <!-- First column contains title / category and points -->
              <div class="column is-flex w-full">
                <div class="contains-title-and-category">
                  <p class="is-category">{{ i + 1 }} - {{ controlPoint.category }}</p>
                  <p class="is-title">{{ controlPoint.title }}</p>
                </div>

                <div class="contains-points is-flex">
                  <ControlPoint 
                    v-for="(point, i2) in controlPoint.points" 
                    :key="`point-${controlPoint.id}-${point.value}`"
                    :name="`point-${controlPoint.id}-${i2}`"
                    :id="`point-${controlPoint.id}-${point.value}`"
                    :label="point.label"
                    :value="point.value"
                    :checked="point.selected"
                    @change="handleTogglePoint(controlPoint.id, point.value)"
                  />
                </div>
              </div>

              <!-- Second column contains action checkbox and comment/pictures buttons -->
              <div class="column is-checkbox is-flex has-item-centered">
                <Checkbox
                  :id="`check-line-${controlPoint.id}-${i}`"
                  :modelValue="controlPoint.checked"
                  @update:modelValue="handleCheckLine(controlPoint.id)"
                />
              </div>

              <div class="column contains-comments-and-pictures is-flex">
                <p 
                  :class="['comment', { active: controlPoint.commentPane.open || controlPoint.commentPane.value }]" 
                  @click="handleToggleCommentPane(controlPoint.id)"
                >
                  <i class="ico comment"></i>
                  <i v-if="controlPoint.commentPane.value" class="has-comment"></i>
                </p>
                <p 
                  :class="['pictures', { active: controlPoint.picturesPane.open || controlPoint.picturesPane.pictures.length > 0 }]" 
                  @click="handleTogglePicturePane(controlPoint.id)"
                >
                  <i class="ico photo"></i>
                  <i v-if="controlPoint.picturesPane.pictures.length > 0" class="has-comment"></i>
                </p>
              </div>
            </div>

            <!-- Comment pane -->
            <div :class="['is-comment-pane', { open: controlPoint.commentPane.open, close: !controlPoint.commentPane.open }]">
              <textarea 
                placeholder="Vos observations ..." 
                :value="controlPoint.commentPane.value" 
                @input="handleChangeComment(controlPoint.id, $event.target.value)"
              ></textarea>
              <div class="is-flex has-item-ended is-button-group">
                <button type="button" class="inline-flex items-center justify-center font-semibold border border-primview-400 text-primview-400 rounded bg-white hover:border-primview-500 hover:text-primview-500 transition-all duration-300 text-base w-36 h-10" @click="handleToggleCommentPane(controlPoint.id)">
                  Fermer
                </button>
              </div>
            </div>

            <!-- Pictures pane -->
            <div :class="['is-pictures-pane', { open: controlPoint.picturesPane.open, close: !controlPoint.picturesPane.open }]">
              <div class="is-flex has-images has-item-started">
                <div 
                  v-for="picture in controlPoint.picturesPane.pictures" 
                  :key="`image-n-${controlPoint.id}-${picture.id}`"
                  class="is-wrapping-image"
                >
                  <img :src="picture.file.src" :alt="`Complément n° ${controlPoint.id}-${picture.id}`" />
                  <i class="ico trash" @click="handleRemoveControlPointImage(controlPoint.id, picture.id)"></i>
                </div>
                <UploadImage 
                  :id="`image-for-${controlPoint.id}`" 
                  @click="handleDisplayCamera(controlPoint.id, false, false)"
                />
              </div>
              <div class="is-flex has-item-ended is-button-group">
                <button type="button" class="inline-flex items-center justify-center font-semibold border border-primview-400 text-primview-400 rounded bg-white hover:border-primview-500 hover:text-primview-500 transition-all duration-300 text-base w-36 h-10" @click="handleTogglePicturePane(controlPoint.id)">
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Summary section -->
        <div class="summary">
          <h2>Résumé de la visite</h2>

          <div class="line">
            <p class="sub-title">Le logement est relouable ...</p>
            <hr />
            <div class="is-flex has-item-started">
              <Radio 
                v-for="(point, i) in summary.rentable" 
                :key="`rent-quality-${i}`"
                name="summary-rent-quality"
                :id="`rent-quality-${i}`"
                :label="point.label"
                :value="point.value"
                :modelValue="point.selected ? point.value : null"
                @update:modelValue="handleSelectRentQuality(i)"
              />
            </div>
          </div>

          <div class="line">
            <div class="is-flex">
              <p class="sub-title">Y a-t-il du désordre / des travaux à prévoir ?</p>
              <div class="contains-comments-and-pictures">
                <p 
                  :class="['pictures', { active: summary.restoration.picturesPane.open || summary.restoration.picturesPane.pictures.length > 0 }]" 
                  @click="handleToggleRestorationImage"
                >
                  <i class="ico photo"></i>
                  <i v-if="summary.restoration.picturesPane.pictures.length > 0" class="has-comment"></i>
                </p>
              </div>
            </div>

            <hr />

            <!-- Restoration pictures pane -->
            <div :class="['is-pictures-pane', { open: summary.restoration.picturesPane.open, close: !summary.restoration.picturesPane.open }]">
              <div class="is-flex has-images has-item-started">
                <div 
                  v-for="picture in summary.restoration.picturesPane.pictures" 
                  :key="`summary-image-n-${picture.id}`"
                  class="is-wrapping-image"
                >
                  <img :src="picture.file.src" :alt="`Complément n° ${picture.id}`" />
                  <i class="ico trash" @click="handleRemoveRestorationImage(picture.id)"></i>
                </div>
                <UploadImage 
                  id="summary-image" 
                  @click="handleDisplayCamera(propertyId, true, false)"
                />
              </div>
              <div class="is-flex has-item-ended">
                <button type="button" class="inline-flex items-center justify-center font-semibold border border-primview-400 text-primview-400 rounded bg-white hover:border-primview-500 hover:text-primview-500 transition-all duration-300 text-base w-36 h-10" @click="handleToggleRestorationImage">
                  Fermer
                </button>
              </div>
              <hr />
            </div>

            <div class="is-flex has-item-started">
              <ControlPoint 
                v-for="(point, i) in summary.restoration.inSummary" 
                :key="`summary-restoration-${i}`"
                name="summary-restoration"
                :id="`summary-restoration-${i}`"
                :label="point.label"
                :value="point.value"
                :checked="point.selected"
                :required="true"
                @change="handleSelectRestorationPoint(i)"
              />
            </div>

            <textarea
              placeholder="Votre commentaire..."
              :value="summary.restoration.comment"
              @input="handleChangeSummaryComment($event.target.value)"
            ></textarea>
          </div>
          
          <!-- Offline information -->
          <div v-if="isOffline" class="information">
            <p class="indication">
              Si vous n'avez pas de connexion Internet et que votre visite comporte des actions 
              alors elle apparaitra dans l'onglet "actions & travaux" que lorsque vous aurez 
              retrouvé une connexion Internet.
            </p>
            <div class="icon">
              <img src="/images/icon.svg" alt="icone"/>
            </div>
          </div>
          
          <!-- Validation errors -->
          <div v-if="!isValid" class="error-information has-text-danger">
            <p>La validation de la visite est impossible car la trame de visite n'est pas complète :</p>
            <ul>
              <li>
                <span class="red-point">•</span>&nbsp; Soit un point de contrôle n'est pas renseigné : Merci de compléter
              </li>
              <li>
                <span class="red-point">•</span>&nbsp; Soit la zone " Votre commentaire..." est vide : Merci de remplir le commentaire ou d'inscrire "RAS" le cas échéant.
              </li>
            </ul>
          </div>
          
          <div class="is-buttons is-flex has-item-ended">
            <VButton
              type="submit"
              :class="['is-large', { 'is-loading': sending, 'is-danger': !isValid }]"
              :disabled="!isValid || sending"
            >
              Valider la visite
            </VButton>
          </div>
        </div>
      </div>
    </form>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import VButton from '@/components/Widget/Button.vue'
import Checkbox from '@/components/Widget/Checkbox.vue'
import CloseButton from '@/components/Widget/CloseButton.vue'
import ControlPoint from '@/components/Widget/ControlPoint.vue'
import Radio from '@/components/Widget/Radio.vue'
import UploadImage from '@/components/Widget/UploadImage.vue'

export default {
  name: 'NewVisit',
  
  components: {
    VButton,
    Checkbox,
    CloseButton,
    ControlPoint,
    Radio,
    UploadImage
  },
  
  computed: {
    ...mapState('newVisit', ['sending', 'messageError', 'messageSuccess', 'loading']),
    ...mapGetters('newVisit', ['controlPoints', 'summary', 'valid']),
    ...mapState('initialLoading', ['offline']),
    
    propertyId() {
      return this.$route.params.id
    },
    
    isOffline() {
      return this.offline
    },
    
    isValid() {
      return this.valid
    }
  },
  
  methods: {
    ...mapActions('newVisit', [
      'fetchControlPoints',
      'toggleControlPoint',
      'checkControlPoint',
      'toggleControlPointComment',
      'changeControlPointComment',
      'toggleControlPointPicture',
      'deleteControlPointPicture',
      'selectRentablePoint',
      'selectRestorationPoint',
      'changeRestorationComment',
      'toggleRestorationPicture',
      'removeRestorationPicture',
      'removeImageError',
      'removeSuccessMessage',
      'send',
      'clean'
    ]),
    ...mapActions('cameraViewer', {
      openCamera: 'open'
    }),
    
    handleTogglePoint(id, value) {
      this.checkControlPoint({ id, value })
    },
    
    handleCheckLine(id) {
      this.toggleControlPoint(id)
    },
    
    handleToggleCommentPane(id, event) {
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }
      this.toggleControlPointComment(id)
    },
    
    handleChangeComment(id, value) {
      this.changeControlPointComment({ id, value })
    },
    
    handleTogglePicturePane(id, event) {
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }
      this.toggleControlPointPicture(id)
    },
    
    handleRemoveControlPointImage(id, pictureId) {
      this.deleteControlPointPicture({ id, pictureId })
    },
    
    handleSelectRentQuality(index) {
      this.selectRentablePoint(index)
    },
    
    handleSelectRestorationPoint(index) {
      this.selectRestorationPoint(index)
    },
    
    handleChangeSummaryComment(value) {
      this.changeRestorationComment(value)
    },
    
    handleToggleRestorationImage(event) {
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }
      this.toggleRestorationPicture()
    },
    
    handleRemoveRestorationImage(id) {
      this.removeRestorationPicture(id)
    },
    
    handleDisplayCamera(id, isRestoration, isAction) {
      // Ouvrir la caméra viewer
      
      if (this.openCamera) {
        this.openCamera({ id, isRestoration, isAction })
      } else {
        console.error('[NewVisit] openCamera is not defined!')
      }
    },
    
    handleRemoveError() {
      this.removeImageError()
    },
    
    handleRemoveSuccess() {
      this.removeSuccessMessage()
    },
    
    async handleSubmit() {
      if (!this.isValid) return
      
      try {
        const result = await this.send({ folderId: this.propertyId })
        
        if (result.success) {
          // Rediriger vers la timeline
          this.$router.push(`/dossier/${this.propertyId}/timeline`)
        }
      } catch (error) {
        console.error('Erreur lors de la soumission:', error)
      }
    }
  },
  
  async mounted() {
    // Charger les points de contrôle
    await this.fetchControlPoints(this.propertyId)
  },
  
  beforeUnmount() {
    // Nettoyer l'état
    this.clean()
  }
}
</script>
